import {
  Body1,
  Card,
  Display1,
  Grid,
  GridItem,
  Label2,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@sainsburys-tech/fable'
import { TableHeader } from '@sainsburys-tech/fable'
import { AlertCircleFilled, Clock, Edit, Tick } from '@sainsburys-tech/icons'
import moment from 'moment'
import React from 'react'

import { groupBy } from '../../../utils/arrays'
import { ICounterTemperature } from '../../types/ICounterTemperature'
import { ICounterTemperatures } from '../../types/ICounterTemperatures'

moment.locale('en-GB')

interface IProps {
  counterTemperature: ICounterTemperatures
}

const CounterTemperatureTable = (props: IProps) => {
  const getCounterName = () => (
    <Display1 style={{ marginTop: '8px' }}>
      Turbo-serve {props.counterTemperature.cabinetNumber}
    </Display1>
  )

  const getTableHeader = (showFifthCell: boolean) => (
    <TableHeader>
      <TableRow>
        <TableCell className='c-hotfood-report-counter-column-mobile'></TableCell>
        <TableCell className='c-hotfood-report-counter-column-mobile'>
          <Label2>Time</Label2>
        </TableCell>
        <TableCell className='c-hotfood-report-counter-column-mobile'>
          <Label2>1</Label2>
        </TableCell>
        <TableCell className='c-hotfood-report-counter-column-mobile'>
          <Label2>2</Label2>
        </TableCell>
        <TableCell className='c-hotfood-report-counter-column-mobile'>
          <Label2>3</Label2>
        </TableCell>
        <TableCell className='c-hotfood-report-counter-column-mobile'>
          <Label2>4</Label2>
        </TableCell>
        {showFifthCell && (
          <TableCell className='c-hotfood-report-counter-column-mobile'>
            <Label2>5</Label2>
          </TableCell>
        )}
      </TableRow>
    </TableHeader>
  )

  const getTableRows = (
    cabinetTemperatures: ICounterTemperature[],
    showFifthCell: boolean,
  ) =>
    cabinetTemperatures.map((ct: ICounterTemperature, i: number) => (
      <TableRow
        key={i}
        data-testid={`row-${i}`}
        className={`${ct.isValid ? '' : 'var(--fable-color-text-error)'}`}
      >
        <TableCell
          className='c-hotfood-report-counter-column-mobile'
          style={{
            width: '30px',
            textAlign: 'center',
            verticalAlign: 'middle',
          }}
        >
          {!!ct.takenAtOverdueTimeMessage && (
            <Clock
              width={20}
              height={20}
              style={{ color: 'var(--fable-color-text-error)' }}
            />
          )}
        </TableCell>
        <TableCell className='c-hotfood-report-counter-column-mobile'>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            <span
              style={{
                color: ct.takenAtOverdueTimeMessage
                  ? 'var(--fable-color-text-error)'
                  : 'inherit',
              }}
            >
              {moment.utc(ct.takenAt).local().format('H:mm')}
              {!!ct.takenAtOverdueTimeMessage &&
                ` (${ct.takenAtOverdueTimeMessage})`}
            </span>
            {!ct.isBluetoothReading && (
              <Edit
                data-testid='manual-check-icon'
                width={20}
                height={20}
                style={{ color: 'var(--fable-color-text-information)' }}
              />
            )}
          </div>
        </TableCell>
        <TableCell className='c-hotfood-report-counter-column-mobile'>
          <Body1>{ct.temperature1 ? `${ct.temperature1}℃` : 'N/A'}</Body1>
        </TableCell>
        <TableCell className='c-hotfood-report-counter-column-mobile'>
          <Body1>{ct.temperature2 ? `${ct.temperature2}℃` : 'N/A'}</Body1>
        </TableCell>
        <TableCell className='c-hotfood-report-counter-column-mobile'>
          <Body1>{ct.temperature3 ? `${ct.temperature3}℃` : 'N/A'}</Body1>
        </TableCell>
        <TableCell className='c-hotfood-report-counter-column-mobile'>
          <Body1>{ct.temperature4 ? `${ct.temperature4}℃` : 'N/A'}</Body1>
        </TableCell>
        {showFifthCell && (
          <TableCell className='c-hotfood-report-counter-column-mobile'>
            <Body1>{ct.temperature5 ? `${ct.temperature5}℃` : 'N/A'}</Body1>
          </TableCell>
        )}
      </TableRow>
    ))

  const hasTemperature5 = (
    counterTemperature: ICounterTemperatures,
  ): boolean => {
    return counterTemperature.cabinetTemperatures.some(
      (temperature: ICounterTemperature) =>
        parseFloat(temperature.temperature5) > 0,
    )
  }

  const renderCounterTemperatures = () => {
    const cabinetTemperatures = props.counterTemperature.cabinetTemperatures
    let previousCabinetTemperature = null
    let displayIndexCount = 0

    // divide into displayIndexes where each one represents a fault, fix or table of temperatures
    for (const cabinetTemperature of cabinetTemperatures) {
      if (
        previousCabinetTemperature === null ||
        previousCabinetTemperature.isFaulty !== cabinetTemperature.isFaulty
      ) {
        displayIndexCount++
      }
      cabinetTemperature.displayIndex = displayIndexCount
      previousCabinetTemperature = cabinetTemperature
    }

    // group by displayIndexes
    const grouped = groupBy(
      cabinetTemperatures,
      (c: ICounterTemperature) => c.displayIndex,
    )

    return Array.from(grouped).map(
      ([, values]: [number, ICounterTemperature[]], index) => {
        if (
          values.length === 1 &&
          values[0].faultyOrFixedText !== null &&
          values[0].faultyOrFixedText.length > 0
        ) {
          return (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem',
                color: values[0].isFaulty
                  ? 'var(--fable-color-text-error)'
                  : 'inherit',
              }}
            >
              {values[0].isFaulty && (
                <AlertCircleFilled width={20} height={20} />
              )}
              {!values[0].isFaulty && (
                <Tick
                  width={20}
                  height={20}
                  style={{
                    backgroundColor: '#2e7d32',
                    color: 'var(--fable-color-background-secondary-default)',
                  }}
                />
              )}
              <span>
                <Body1 style={{ fontWeight: '700' }}>
                  {values[0].faultyOrFixedText}
                </Body1>
              </span>
            </div>
          )
        }
        return (
          <Table
            key={index}
            testid='counter-temperature-table'
            className='c-counter-temperature-table'
          >
            {getTableHeader(hasTemperature5(props.counterTemperature))}
            <TableBody>
              {getTableRows(values, hasTemperature5(props.counterTemperature))}
            </TableBody>
          </Table>
        )
      },
    )
  }

  return (
    <Card orientation='vertical'>
      <Grid gap='md'>
        <GridItem colSpan={12}>
          <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
            {getCounterName()}
          </div>
        </GridItem>

        <GridItem colSpan={12}>{renderCounterTemperatures()}</GridItem>

        {(!props.counterTemperature.isValid ||
          props.counterTemperature.cabinetSummaryHighlighted ||
          props.counterTemperature.cabinetSummary) && (
          <GridItem colSpan={12}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0',
              }}
            >
              {!props.counterTemperature.isValid && (
                <AlertCircleFilled
                  width={20}
                  height={20}
                  style={{ color: 'var(--fable-color-text-error)' }}
                />
              )}
              {props.counterTemperature.cabinetSummaryHighlighted && (
                <span
                  style={{
                    color: 'var(--fable-color-text-error)',
                    fontWeight: 'bold',
                  }}
                >
                  {props.counterTemperature.cabinetSummaryHighlighted}
                </span>
              )}
              {props.counterTemperature.cabinetSummary && (
                <span>
                  <Body1 style={{ fontWeight: '700' }}>
                    {props.counterTemperature.cabinetSummary}
                  </Body1>
                </span>
              )}
            </div>
          </GridItem>
        )}
      </Grid>
    </Card>
  )
}

export default CounterTemperatureTable
